import { translate } from '../../../i18n';
import {
    MAX_BOOKING_CAPACITY,
    MIN_BOOKING_CAPACITY,
} from '../../../utils/sport-capacity';
import { capacityIssueMessage } from './capacity-issue-message';
import {
    AvailabilityCapacityIssue,
    AvailabilityPricingConfig,
    BLOCKING_CAPACITY_ISSUE_KINDS,
    findAvailabilityCapacityIssues,
    hasInvalidAvailabilityPrice,
} from './validate-availability-prices';

/**
 * The one reason a set of availability pricing rows cannot be saved, or `null`.
 *
 * ## Why this exists
 *
 * Four screens write pricing rows — `AddAvailabilitiesModal`, `CreateCourtForm`,
 * `AddAvailabilitiesForm` and `CreateBlockadeOrCourtAvailabilityModal` — and
 * each hand-rolled the same two guards in the same order. The fourth one never
 * did, for 78 routine runs: the calendar's blockade/availability modal sent
 * whatever the operator typed and let the API's 400 be the whole explanation.
 * Duplicating a guard across four call sites is how one of them ends up
 * missing it, so the guard now has one home and the call sites ask it a
 * question.
 *
 * ## Order is part of the contract
 *
 * Price first, capacity second — the order the existing call sites already
 * used. A form with both problems reports the price one, because that is the
 * field the operator most likely left empty and the capacity message would
 * send them to the wrong control.
 */
export type AvailabilitySubmitBlocker =
    | { kind: 'invalid-price' }
    | { kind: 'capacity'; issue: AvailabilityCapacityIssue };

/** Bounds every money path enforces; mirrors the API's own check. */
const CAPACITY_BOUNDS = {
    minCapacity: MIN_BOOKING_CAPACITY,
    maxCapacity: MAX_BOOKING_CAPACITY,
};

/**
 * The first blocking problem in a batch of availability pricing rows.
 *
 * @param configurationGroups ONE array per availability block. Overlapping
 * availabilities may legitimately repeat a (sport, duration, capacity) triple
 * and the API groups the same way, so a flattened list would refuse schedules
 * the server accepts.
 * @param resolvedPrices every price as it will be SENT — apply any
 * `?? courtDefault` fallback before calling, because that is what the API
 * receives and therefore what it validates.
 */
export function findAvailabilitySubmitBlocker(
    configurationGroups: AvailabilityPricingConfig[][],
    resolvedPrices: (number | undefined | null)[],
): AvailabilitySubmitBlocker | null {
    if (hasInvalidAvailabilityPrice(resolvedPrices)) {
        return { kind: 'invalid-price' };
    }

    const blockingIssue = findAvailabilityCapacityIssues(
        configurationGroups,
        CAPACITY_BOUNDS,
    ).find((issue) => BLOCKING_CAPACITY_ISSUE_KINDS.includes(issue.kind));

    return blockingIssue ? { kind: 'capacity', issue: blockingIssue } : null;
}

/**
 * What to show the operator, and for how long.
 *
 * The duration is here rather than at the call sites because it was already
 * identical at all of them (4 s for a price, 6 s for a capacity — the capacity
 * messages name a number and take longer to read), and because a shared guard
 * whose message length and display time disagree between screens is the same
 * duplication problem one layer down.
 */
export function availabilitySubmitBlockerToast(
    blocker: AvailabilitySubmitBlocker,
): { title: string; durationMs: number } {
    if (blocker.kind === 'invalid-price') {
        return { title: translate('courts.invalid_price'), durationMs: 4000 };
    }

    return { title: capacityIssueMessage(blocker.issue), durationMs: 6000 };
}
